import { LitElement, html, type TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import {
  PLATFORM_LIST,
  INTEGRATION_LIST,
  type IntegrationMeta,
} from "../components/platform-icons.ts";

const FEATURE_TABS = [
  { id: "desktop", label: "Desktop", accent: "#3B82F6" },
  { id: "messaging", label: "Chat", accent: "#10B981" },
  { id: "memory", label: "Memory", accent: "#8B5CF6" },
  { id: "agents", label: "Agents", accent: "#F59E0B" },
  { id: "tools", label: "Tools", accent: "#EC4899" },
  { id: "voice", label: "Voice", accent: "#06B6D4" },
] as const;

@customElement("katanoid-landing")
export class KatanoidLanding extends LitElement {
  private _featureObserver: IntersectionObserver | null = null;
  private _activeFeature: (typeof FEATURE_TABS)[number]["id"] = "desktop";
  private _autoAdvanceTimer: ReturnType<typeof setInterval> | null = null;
  private _swipeStartX = 0;
  private _progressKey = 0; // bumped to restart progress animation

  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Wait for first render so DOM is available
    requestAnimationFrame(() => {
      this._initScrollReveal();
      this._initFeatureTabs();
      this._initSwipeGesture();
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._featureObserver?.disconnect();
    this._featureObserver = null;
    if (this._autoAdvanceTimer) clearInterval(this._autoAdvanceTimer);
  }

  // ── Scroll reveal (for non-feature sections) ──

  private _initScrollReveal(): void {
    const cards = this.querySelectorAll(".landing-feature");
    if (!cards.length) return;

    // Respect reduced-motion preference
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      cards.forEach((c) => c.classList.add("is-visible"));
      return;
    }

    // The scroll container is .landing (overflow-y: auto), not the viewport
    const scrollRoot = this.querySelector(".landing");

    this._featureObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            this._featureObserver?.unobserve(entry.target);
          }
        }
      },
      {
        root: scrollRoot,
        threshold: 0.12,
        rootMargin: "0px 0px -50px 0px",
      },
    );

    cards.forEach((c) => this._featureObserver!.observe(c));
  }

  // ── Feature tab showcase ──

  private _initFeatureTabs(): void {
    // Don't auto-advance if reduced motion is preferred
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    this._startAutoAdvance();
    this._updateIndicator();
  }

  private _selectFeature(id: (typeof FEATURE_TABS)[number]["id"]): void {
    if (this._activeFeature === id) return;
    this._activeFeature = id;
    this._progressKey++;
    this._restartAutoAdvance();
    this._updatePanels();
    this._updateTabs();
    this._updateIndicator();
    this._updateProgressBar();
  }

  private _updatePanels(): void {
    const panels = this.querySelectorAll<HTMLElement>(".feature-panel");
    panels.forEach((p) => {
      const isActive = p.dataset.feature === this._activeFeature;
      p.classList.toggle("feature-panel--active", isActive);
    });
  }

  private _updateTabs(): void {
    const tabs = this.querySelectorAll<HTMLElement>(".feature-tab");
    tabs.forEach((t) => {
      const isActive = t.dataset.featureTab === this._activeFeature;
      t.classList.toggle("feature-tab--active", isActive);
      t.setAttribute("aria-selected", String(isActive));
    });
  }

  private _updateIndicator(): void {
    const activeTab = this.querySelector<HTMLElement>(
      `.feature-tab[data-feature-tab="${this._activeFeature}"]`,
    );
    const indicator = this.querySelector<HTMLElement>(".feature-tabs__indicator");
    if (!activeTab || !indicator) return;

    const tabsRect = activeTab.parentElement!.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    indicator.style.left = `${tabRect.left - tabsRect.left}px`;
    indicator.style.width = `${tabRect.width}px`;
    indicator.style.background = activeTab.style.getPropertyValue("--tab-accent");
  }

  private _updateProgressBar(): void {
    const bar = this.querySelector<HTMLElement>(".feature-tabs__progress");
    if (!bar) return;
    // Restart the CSS animation by toggling the data attribute
    bar.setAttribute("data-key", String(this._progressKey));
  }

  private _startAutoAdvance(): void {
    this._autoAdvanceTimer = setInterval(() => {
      const ids = FEATURE_TABS.map((f) => f.id);
      const idx = ids.indexOf(this._activeFeature);
      const next = ids[(idx + 1) % ids.length];
      this._selectFeature(next);
    }, 5000);
  }

  private _restartAutoAdvance(): void {
    if (this._autoAdvanceTimer) clearInterval(this._autoAdvanceTimer);
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this._startAutoAdvance();
    }
  }

  private _handleShowcaseEnter = (): void => {
    if (this._autoAdvanceTimer) {
      clearInterval(this._autoAdvanceTimer);
      this._autoAdvanceTimer = null;
    }
  };

  private _handleShowcaseLeave = (): void => {
    this._restartAutoAdvance();
  };

  private _initSwipeGesture(): void {
    const showcase = this.querySelector(".feature-showcase");
    if (!showcase) return;
    showcase.addEventListener(
      "touchstart",
      (e: Event) => {
        this._swipeStartX = (e as TouchEvent).touches[0].clientX;
      },
      { passive: true },
    );
    showcase.addEventListener(
      "touchend",
      (e: Event) => {
        const dx = (e as TouchEvent).changedTouches[0].clientX - this._swipeStartX;
        if (Math.abs(dx) < 50) return;
        const ids = FEATURE_TABS.map((f) => f.id);
        const idx = ids.indexOf(this._activeFeature);
        const next =
          dx < 0 ? ids[(idx + 1) % ids.length] : ids[(idx - 1 + ids.length) % ids.length];
        this._selectFeature(next);
      },
      { passive: true },
    );
  }

  protected override render(): TemplateResult {
    return html`
      <div class="landing">
        ${this.renderNav()}
        ${this.renderHero()}
        ${this.renderFeatures()}
        ${this.renderChannels()}
        ${this.renderPricing()}
        ${this.renderFinalCta()}
        ${this.renderFooter()}
      </div>
    `;
  }

  // ── Nav ──

  private renderNav(): TemplateResult {
    return html`
      <nav class="landing-nav">
        <div class="landing-nav__inner">
          <div class="landing-nav__brand">
            <svg class="landing-nav__logo" viewBox="0 0 48 48" fill="none">
              <path d="M24 4L6 14v20l18 10 18-10V14L24 4z" fill="currentColor" opacity="0.9"/>
              <path d="M24 4L6 14l18 10 18-10L24 4z" fill="currentColor"/>
            </svg>
            <span class="landing-nav__wordmark">Katanoid</span>
          </div>
          <div class="landing-nav__actions">
            <button class="landing-theme-toggle" @click=${this._toggleTheme} aria-label="Toggle dark mode">
              <svg class="landing-theme-toggle__sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              <svg class="landing-theme-toggle__moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            </button>
            <button class="btn btn--ghost btn--sm" @click=${this.handleSignIn}>Sign In</button>
            <button class="landing-cta-btn landing-cta-btn--sm" @click=${this.handleGetStarted}>Get Started</button>
          </div>
        </div>
      </nav>
    `;
  }

  private renderShutdownNotice(): TemplateResult {
    return html`
      <div class="landing-shutdown-notice">
        <div class="landing-shutdown-notice__icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div class="landing-shutdown-notice__content">
          <span class="landing-shutdown-notice__badge">Important Notice</span>
          <p class="landing-shutdown-notice__text">
            We are no longer operating. All user data has now been permanently deleted.
          </p>
        </div>
      </div>
    `;
  }

  // ── Hero ──

  private renderHero(): TemplateResult {
    // Floating platform icons around the hero
    const floaters = [
      {
        svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
        color: "#25D366",
        pos: "top: 15%; left: 8%",
        delay: "0s",
        size: "38px",
      },
      {
        svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 2L11 13"/><path d="m22 2-7 20-4-9-9-4 20-7z"/></svg>`,
        color: "#229ED9",
        pos: "top: 25%; right: 6%",
        delay: "1.5s",
        size: "34px",
      },
      {
        svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>`,
        color: "#F59E0B",
        pos: "bottom: 30%; left: 5%",
        delay: "3s",
        size: "32px",
      },
      {
        svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>`,
        color: "#5865F2",
        pos: "bottom: 20%; right: 8%",
        delay: "2s",
        size: "36px",
      },
      {
        svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/></svg>`,
        color: "#8B5CF6",
        pos: "top: 60%; left: 12%",
        delay: "4s",
        size: "30px",
      },
      {
        svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>`,
        color: "#EC4899",
        pos: "top: 55%; right: 10%",
        delay: "5s",
        size: "28px",
      },
    ];

    return html`
      <section class="landing-hero">
        <div class="landing-hero__orb landing-hero__orb--blue"></div>
        <div class="landing-hero__orb landing-hero__orb--purple"></div>
        <div class="landing-hero__orb landing-hero__orb--pink"></div>

        ${floaters.map(
          (f) => html`
          <div class="landing-floater" style="${f.pos}; color: ${f.color}; animation-delay: ${f.delay}; width: ${f.size}; height: ${f.size}">
            <span .innerHTML=${f.svg}></span>
          </div>
        `,
        )}

        ${this.renderShutdownNotice()}

        <p class="landing-hero__eyebrow">
          <span class="landing-hero__eyebrow-dot"></span>
          Now available on macOS, Windows & Linux
        </p>
        <h1 class="landing-hero__title">
          <span class="landing-hero__title-light">The</span>
          <span class="landing-gradient-text">AI layer</span>
          <span class="landing-hero__title-light">for your</span>
          entire digital life
        </h1>
        <p class="landing-hero__subtitle">
          One AI assistant that lives on your devices and connects to every platform you use.
          Chat on WhatsApp, Telegram, Discord, and more. Automate tasks. Run agents. All from one place.
        </p>
        <div class="landing-hero__cta">
          <button class="landing-cta-btn landing-cta-btn--lg" @click=${this.handleGetStarted}>
            Get Started Free
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </button>
          <p class="landing-hero__note">No credit card required</p>
        </div>
      </section>
    `;
  }

  // ── Stats strip ──

  // ── Features (tabbed showcase) ──

  private renderFeatures(): TemplateResult {
    return html`
      <section class="landing-section landing-section--features">
        <p class="landing-section__eyebrow">Features</p>
        <h2 class="landing-section__title">Everything you need, <span class="landing-gradient-text">one subscription</span></h2>
        <p class="landing-section__subtitle">Every platform. Endlessly extensible.</p>

        <!-- Tab bar -->
        <div class="feature-tabs" role="tablist">
          ${FEATURE_TABS.map(
            (f) => html`
              <button
                class="feature-tab ${f.id === "desktop" ? "feature-tab--active" : ""}"
                role="tab"
                aria-selected=${f.id === "desktop" ? "true" : "false"}
                data-feature-tab="${f.id}"
                style="--tab-accent: ${f.accent}"
                @click=${() => this._selectFeature(f.id)}
              >
                <span class="feature-tab__dot"></span>
                <span class="feature-tab__label">${f.label}</span>
              </button>
            `,
          )}
          <div class="feature-tabs__indicator"></div>
          <div class="feature-tabs__progress" data-key="0"></div>
        </div>

        <!-- Showcase: one panel visible at a time -->
        <div
          class="feature-showcase"
          @mouseenter=${this._handleShowcaseEnter}
          @mouseleave=${this._handleShowcaseLeave}
        >

          <!-- Panel: Desktop -->
          <div class="feature-panel feature-panel--active" data-feature="desktop" style="--card-accent: #3B82F6">
            <div class="landing-feature__glow"></div>
            <div class="bento-illus-desktop__scene">
              <div class="bento-illus-desktop__titlebar">
                <span class="bento-illus-desktop__dot bento-illus-desktop__dot--r"></span>
                <span class="bento-illus-desktop__dot bento-illus-desktop__dot--y"></span>
                <span class="bento-illus-desktop__dot bento-illus-desktop__dot--g"></span>
                <div class="bento-illus-desktop__tab bento-illus-desktop__tab--active"></div>
                <div class="bento-illus-desktop__tab"></div>
              </div>
              <div class="bento-illus-desktop__layout">
                <div class="bento-illus-desktop__sidebar">
                  <div class="bento-illus-desktop__nav-dot"></div>
                  <div class="bento-illus-desktop__nav-dot"></div>
                  <div class="bento-illus-desktop__nav-dot"></div>
                  <div class="bento-illus-desktop__nav-label"></div>
                  <div class="bento-illus-desktop__nav-label bento-illus-desktop__nav-label--short"></div>
                  <div class="bento-illus-desktop__nav-label"></div>
                </div>
                <div class="bento-illus-desktop__content">
                  <div class="landing-feature__body">
                    <h3 class="landing-feature__title">Desktop <span class="landing-feature__accent">companion</span> apps</h3>
                    <p class="landing-feature__desc">Native apps for macOS, Windows & Linux, already available. Mobile apps are on the way. AI on your machine, in your terminal, in your workflow.</p>
                    <div class="landing-feature__pills">
                      <span class="landing-feature__pill">macOS</span>
                      <span class="landing-feature__pill">Windows</span>
                      <span class="landing-feature__pill">Linux</span>
                      <span class="landing-feature__pill landing-feature__pill--soon">Mobile soon</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bento-illus-desktop__float bento-illus-desktop__float--code">
                <div class="bento-illus-desktop__titlebar">
                  <span class="bento-illus-desktop__dot bento-illus-desktop__dot--r"></span>
                  <span class="bento-illus-desktop__dot bento-illus-desktop__dot--y"></span>
                  <span class="bento-illus-desktop__dot bento-illus-desktop__dot--g"></span>
                </div>
                <div class="bento-illus-desktop__body bento-illus-desktop__body--code">
                  <div class="bento-illus-desktop__code-line bento-illus-desktop__code-line--pink"></div>
                  <div class="bento-illus-desktop__code-line bento-illus-desktop__code-line--blue"></div>
                  <div class="bento-illus-desktop__code-line bento-illus-desktop__code-line--green"></div>
                  <div class="bento-illus-desktop__code-line bento-illus-desktop__code-line--pink bento-illus-desktop__code-line--short"></div>
                  <div class="bento-illus-desktop__cursor"></div>
                </div>
              </div>
              <div class="bento-illus-desktop__float bento-illus-desktop__float--notif">
                <div class="bento-illus-desktop__notif-dot"></div>
                <div class="bento-illus-desktop__notif-lines">
                  <div class="bento-illus-desktop__notif-line"></div>
                  <div class="bento-illus-desktop__notif-line bento-illus-desktop__notif-line--short"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Panel: Messaging -->
          <div class="feature-panel" data-feature="messaging" style="--card-accent: #10B981">
            <div class="landing-feature__glow"></div>
            <div class="bento-illus-chat__app">
              <div class="bento-illus-chat__app-titlebar">
                <span class="bento-illus-chat__dot bento-illus-chat__dot--r"></span>
                <span class="bento-illus-chat__dot bento-illus-chat__dot--y"></span>
                <span class="bento-illus-chat__dot bento-illus-chat__dot--g"></span>
                <div class="bento-illus-chat__app-title-text"></div>
              </div>
              <div class="bento-illus-chat__contact-bar">
                <div class="bento-illus-chat__contact-avatar"></div>
                <div class="bento-illus-chat__contact-name"></div>
                <div class="bento-illus-chat__contact-status"></div>
              </div>
              <div class="bento-illus-chat__thread">
                <div class="bento-illus-chat__row bento-illus-chat__row--left">
                  <div class="bento-illus-chat__avatar bento-illus-chat__avatar--1"></div>
                  <div class="bento-illus-chat__bubble bento-illus-chat__bubble--1">
                    <div class="bento-illus-chat__text-line"></div>
                    <div class="bento-illus-chat__text-line bento-illus-chat__text-line--short"></div>
                  </div>
                </div>
                <div class="bento-illus-chat__row bento-illus-chat__row--right">
                  <div class="bento-illus-chat__bubble bento-illus-chat__bubble--2">
                    <div class="bento-illus-chat__text-line"></div>
                  </div>
                </div>
                <div class="bento-illus-chat__row bento-illus-chat__row--left">
                  <div class="bento-illus-chat__avatar bento-illus-chat__avatar--1"></div>
                  <div class="bento-illus-chat__bubble bento-illus-chat__bubble--3">
                    <div class="bento-illus-chat__text-line"></div>
                    <div class="bento-illus-chat__text-line bento-illus-chat__text-line--medium"></div>
                  </div>
                </div>
                <div class="bento-illus-chat__row bento-illus-chat__row--right bento-illus-chat__row--body">
                  <div class="bento-illus-chat__bubble bento-illus-chat__bubble--body">
                    <div class="landing-feature__body">
                      <h3 class="landing-feature__title">Every <span class="landing-feature__accent">messaging</span> platform</h3>
                      <p class="landing-feature__desc">WhatsApp, Telegram, Discord & Slack today. Signal, iMessage, Matrix & Teams coming soon.</p>
                    </div>
                  </div>
                </div>
                <div class="bento-illus-chat__row bento-illus-chat__row--left">
                  <div class="bento-illus-chat__avatar bento-illus-chat__avatar--1"></div>
                  <div class="bento-illus-chat__typing">
                    <span class="bento-illus-chat__typing-dot"></span>
                    <span class="bento-illus-chat__typing-dot"></span>
                    <span class="bento-illus-chat__typing-dot"></span>
                  </div>
                </div>
              </div>
              <div class="bento-illus-chat__input-bar">
                <div class="bento-illus-chat__input-field"></div>
                <div class="bento-illus-chat__send-btn"></div>
              </div>
            </div>
          </div>

          <!-- Panel: Memory -->
          <div class="feature-panel" data-feature="memory" style="--card-accent: #8B5CF6">
            <div class="landing-feature__glow"></div>
            <div class="bento-illus-memory__app">
              <div class="bento-illus-memory__titlebar">
                <span class="bento-illus-memory__dot bento-illus-memory__dot--r"></span>
                <span class="bento-illus-memory__dot bento-illus-memory__dot--y"></span>
                <span class="bento-illus-memory__dot bento-illus-memory__dot--g"></span>
                <div class="bento-illus-memory__titlebar-text"></div>
              </div>
              <div class="bento-illus-memory__layout">
                <div class="bento-illus-memory__sidebar">
                  <div class="bento-illus-memory__sidebar-header">
                    <div class="bento-illus-memory__sidebar-title"></div>
                    <div class="bento-illus-memory__mini-graph">
                      <span class="bento-illus-memory__mini-dot"></span>
                      <span class="bento-illus-memory__mini-dot"></span>
                      <span class="bento-illus-memory__mini-dot"></span>
                      <span class="bento-illus-memory__mini-link"></span>
                      <span class="bento-illus-memory__mini-link bento-illus-memory__mini-link--2"></span>
                    </div>
                  </div>
                  <div class="bento-illus-memory__entry bento-illus-memory__entry--active">
                    <div class="bento-illus-memory__entry-icon"></div>
                    <div class="bento-illus-memory__entry-lines">
                      <div class="bento-illus-memory__entry-line"></div>
                      <div class="bento-illus-memory__entry-line bento-illus-memory__entry-line--short"></div>
                    </div>
                  </div>
                  <div class="bento-illus-memory__entry">
                    <div class="bento-illus-memory__entry-icon"></div>
                    <div class="bento-illus-memory__entry-lines">
                      <div class="bento-illus-memory__entry-line"></div>
                      <div class="bento-illus-memory__entry-line bento-illus-memory__entry-line--short"></div>
                    </div>
                  </div>
                  <div class="bento-illus-memory__entry">
                    <div class="bento-illus-memory__entry-icon"></div>
                    <div class="bento-illus-memory__entry-lines">
                      <div class="bento-illus-memory__entry-line"></div>
                    </div>
                  </div>
                  <div class="bento-illus-memory__entry">
                    <div class="bento-illus-memory__entry-icon"></div>
                    <div class="bento-illus-memory__entry-lines">
                      <div class="bento-illus-memory__entry-line"></div>
                      <div class="bento-illus-memory__entry-line bento-illus-memory__entry-line--short"></div>
                    </div>
                  </div>
                </div>
                <div class="bento-illus-memory__content">
                  <div class="landing-feature__body">
                    <h3 class="landing-feature__title"><span class="landing-feature__accent">Persistent</span> memory</h3>
                    <p class="landing-feature__desc">Remembers your preferences and context across every conversation and platform. Your assistant learns and adapts over time.</p>
                    <div class="landing-feature__pills">
                      <span class="landing-feature__pill">preferences</span>
                      <span class="landing-feature__pill">context</span>
                      <span class="landing-feature__pill">history</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Panel: Agents -->
          <div class="feature-panel" data-feature="agents" style="--card-accent: #F59E0B">
            <div class="landing-feature__glow"></div>
            <div class="bento-illus-agents__app">
              <div class="bento-illus-agents__titlebar">
                <span class="bento-illus-agents__dot bento-illus-agents__dot--r"></span>
                <span class="bento-illus-agents__dot bento-illus-agents__dot--y"></span>
                <span class="bento-illus-agents__dot bento-illus-agents__dot--g"></span>
                <div class="bento-illus-agents__toolbar">
                  <div class="bento-illus-agents__tool-btn bento-illus-agents__tool-btn--run"></div>
                  <div class="bento-illus-agents__tool-sep"></div>
                  <div class="bento-illus-agents__tool-btn"></div>
                  <div class="bento-illus-agents__tool-btn"></div>
                </div>
              </div>
              <div class="bento-illus-agents__canvas">
                <div class="bento-illus-agents__node bento-illus-agents__node--done">
                  <div class="bento-illus-agents__node-check">&#10003;</div>
                  <div class="bento-illus-agents__node-label"></div>
                </div>
                <div class="bento-illus-agents__edge"></div>
                <div class="bento-illus-agents__node bento-illus-agents__node--done">
                  <div class="bento-illus-agents__node-check">&#10003;</div>
                  <div class="bento-illus-agents__node-label"></div>
                </div>
                <div class="bento-illus-agents__edge"></div>
                <div class="bento-illus-agents__node bento-illus-agents__node--main">
                  <div class="bento-illus-agents__node-pulse"></div>
                  <div class="landing-feature__body">
                    <h3 class="landing-feature__title">Autonomous <span class="landing-feature__accent">AI agents</span> <span class="landing-badge">Coming Soon</span></h3>
                    <p class="landing-feature__desc">Multi-step workflows that research, plan, and execute complex tasks on your behalf. Give your AI a goal and let it figure out the rest.</p>
                  </div>
                </div>
                <div class="bento-illus-agents__branch">
                  <div class="bento-illus-agents__branch-arm">
                    <div class="bento-illus-agents__edge"></div>
                    <div class="bento-illus-agents__node bento-illus-agents__node--pending">
                      <div class="bento-illus-agents__node-ring"></div>
                      <div class="bento-illus-agents__node-label"></div>
                    </div>
                  </div>
                  <div class="bento-illus-agents__branch-arm">
                    <div class="bento-illus-agents__edge"></div>
                    <div class="bento-illus-agents__node bento-illus-agents__node--pending">
                      <div class="bento-illus-agents__node-ring"></div>
                      <div class="bento-illus-agents__node-label"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Panel: Tools -->
          <div class="feature-panel" data-feature="tools" style="--card-accent: #EC4899">
            <div class="landing-feature__glow"></div>
            <div class="bento-illus-tools">
              <div class="bento-illus-tools__terminal">
                <div class="bento-illus-tools__titlebar">
                  <span class="bento-illus-tools__dot bento-illus-tools__dot--r"></span>
                  <span class="bento-illus-tools__dot bento-illus-tools__dot--y"></span>
                  <span class="bento-illus-tools__dot bento-illus-tools__dot--g"></span>
                  <div class="bento-illus-tools__tab-bar">
                    <div class="bento-illus-tools__tab bento-illus-tools__tab--active"></div>
                    <div class="bento-illus-tools__tab"></div>
                  </div>
                </div>
                <div class="bento-illus-tools__body">
                  <div class="bento-illus-tools__line-numbers">
                    <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span><span>6</span><span>7</span><span>8</span>
                  </div>
                  <div class="bento-illus-tools__content">
                    <div class="bento-illus-tools__prompt">
                      <span class="bento-illus-tools__dollar"></span>
                      <div class="bento-illus-tools__cmd bento-illus-tools__cmd--1"></div>
                    </div>
                    <div class="bento-illus-tools__output">
                      <div class="bento-illus-tools__out-line bento-illus-tools__out-line--1"></div>
                      <div class="bento-illus-tools__out-line bento-illus-tools__out-line--2"></div>
                    </div>
                    <div class="bento-illus-tools__prompt">
                      <span class="bento-illus-tools__dollar"></span>
                      <div class="bento-illus-tools__cmd bento-illus-tools__cmd--2"></div>
                    </div>
                    <div class="bento-illus-tools__output">
                      <div class="bento-illus-tools__out-line bento-illus-tools__out-line--3"></div>
                      <div class="bento-illus-tools__out-line bento-illus-tools__out-line--4"></div>
                    </div>
                    <div class="bento-illus-tools__prompt bento-illus-tools__prompt--active">
                      <span class="bento-illus-tools__dollar"></span>
                      <div class="bento-illus-tools__cmd-typing"></div>
                      <div class="bento-illus-tools__cursor"></div>
                    </div>
                  </div>
                </div>
                <div class="bento-illus-tools__divider"></div>
                <div class="landing-feature__body">
                  <h3 class="landing-feature__title"><span class="landing-feature__accent">Custom</span> tools</h3>
                  <p class="landing-feature__desc">Extend your AI with shell scripts and automations that run directly on your device. Build custom workflows tailored to how you work.</p>
                </div>
                <div class="bento-illus-tools__statusbar">
                  <span class="bento-illus-tools__status-dot"></span>
                  <span class="bento-illus-tools__status-dot"></span>
                  <div class="bento-illus-tools__status-label"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Panel: Voice -->
          <div class="feature-panel" data-feature="voice" style="--card-accent: #06B6D4">
            <div class="landing-feature__glow"></div>
            <div class="bento-illus-voice__app">
              <div class="bento-illus-voice__titlebar">
                <span class="bento-illus-voice__dot bento-illus-voice__dot--r"></span>
                <span class="bento-illus-voice__dot bento-illus-voice__dot--y"></span>
                <span class="bento-illus-voice__dot bento-illus-voice__dot--g"></span>
                <div class="bento-illus-voice__titlebar-text"></div>
              </div>
              <div class="bento-illus-voice__listening">
                <div class="bento-illus-voice__orb">
                  <div class="bento-illus-voice__orb-ring"></div>
                  <div class="bento-illus-voice__orb-core"></div>
                </div>
                <div class="bento-illus-voice__waveform">
                  <span class="bento-illus-voice__wave-bar"></span>
                  <span class="bento-illus-voice__wave-bar"></span>
                  <span class="bento-illus-voice__wave-bar"></span>
                  <span class="bento-illus-voice__wave-bar"></span>
                  <span class="bento-illus-voice__wave-bar"></span>
                  <span class="bento-illus-voice__wave-bar"></span>
                  <span class="bento-illus-voice__wave-bar"></span>
                </div>
                <div class="bento-illus-voice__listening-label"></div>
              </div>
              <div class="bento-illus-voice__transcript">
                <div class="bento-illus-voice__msg bento-illus-voice__msg--user">
                  <div class="bento-illus-voice__msg-avatar"></div>
                  <div class="bento-illus-voice__msg-text">
                    <div class="bento-illus-voice__msg-line"></div>
                    <div class="bento-illus-voice__msg-line bento-illus-voice__msg-line--short"></div>
                  </div>
                </div>
                <div class="bento-illus-voice__msg bento-illus-voice__msg--ai">
                  <div class="bento-illus-voice__msg-avatar bento-illus-voice__msg-avatar--ai"></div>
                  <div class="bento-illus-voice__msg-content">
                    <div class="landing-feature__body">
                      <h3 class="landing-feature__title"><span class="landing-feature__accent">Voice</span> input</h3>
                      <p class="landing-feature__desc">Speak naturally to your AI assistant using your browser's microphone. Voice-to-text input with hold-to-talk or toggle modes.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bento-illus-voice__bottom-bar">
                <div class="bento-illus-voice__mic-btn">
                  <div class="bento-illus-voice__mic-icon"></div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    `;
  }

  // ── Channels (marquee) ──

  private renderChannels(): TemplateResult {
    // Split integrations into two rows for dual-marquee
    const row1 = INTEGRATION_LIST.filter(
      (i) => i.category === "messaging" || i.category === "email" || i.category === "platform",
    );
    const row2 = INTEGRATION_LIST.filter(
      (i) =>
        i.category === "calendar" ||
        i.category === "storage" ||
        i.category === "productivity" ||
        i.category === "devtools" ||
        i.category === "custom" ||
        i.category === "ai",
    );

    // Quadruple each row for seamless infinite scroll
    const row1x4 = [...row1, ...row1, ...row1, ...row1];
    const row2x4 = [...row2, ...row2, ...row2, ...row2];

    const renderItem = (item: IntegrationMeta) => html`
      <div class="landing-marquee__item ${item.available ? "" : "landing-marquee__item--soon"}">
        <div class="landing-marquee__icon" style="--ch-color: ${item.color}; color: ${item.color}">
          <span .innerHTML=${item.svg}></span>
        </div>
        <span class="landing-marquee__name">${item.name}</span>
        ${
          item.available
            ? ""
            : html`
                <span class="landing-marquee__soon">Soon</span>
              `
        }
      </div>
    `;

    return html`
      <section class="landing-section landing-section--channels">
        <div class="landing-channels__orb landing-channels__orb--blue"></div>
        <div class="landing-channels__orb landing-channels__orb--purple"></div>
        <p class="landing-section__eyebrow">Integrations</p>
        <h2 class="landing-section__title">Connects to <span class="landing-gradient-text">everything</span> you use</h2>
        <p class="landing-section__subtitle">Messaging, email, calendars, cloud storage, dev tools, and custom APIs — one AI assistant, every platform.</p>
        <div class="landing-marquee">
          <div class="landing-marquee__track">
            ${row1x4.map(renderItem)}
          </div>
        </div>
        <div class="landing-marquee">
          <div class="landing-marquee__track landing-marquee__track--reverse">
            ${row2x4.map(renderItem)}
          </div>
        </div>
      </section>
    `;
  }

  // ── Pricing ──

  private renderPricing(): TemplateResult {
    const plans = [
      {
        name: "Free",
        price: "$0",
        period: "forever",
        highlight: false,
        badge: null,
        features: [
          "50 credits / month",
          "3 messaging channels",
          "Basic memory (7 days)",
          "Community support",
        ],
      },
      {
        name: "BYOK",
        price: "$5",
        period: "/mo",
        highlight: false,
        badge: null,
        features: [
          "Bring your own API key",
          "400+ models via OpenRouter",
          "Unlimited channels",
          "Full memory",
        ],
      },
      {
        name: "Pro",
        price: "$12",
        period: "/mo",
        highlight: true,
        badge: "Most Popular",
        features: [
          "1,000 credits / month",
          "Unlimited channels",
          "Full-power AI models",
          "Web browsing agent",
          "Full persistent memory",
          "Email support",
        ],
      },
      {
        name: "Pro+",
        price: "$24",
        period: "/mo",
        highlight: false,
        badge: "Best Value",
        features: [
          "1,500 credits / month",
          "Everything in Pro",
          "Priority model queue",
          "Multi-assistant support",
          "Early access to beta features",
          "Priority email support",
        ],
      },
    ];

    return html`
      <section class="landing-section landing-section--pricing" id="pricing">
        <p class="landing-section__eyebrow">Pricing</p>
        <h2 class="landing-section__title">Simple, transparent pricing</h2>
        <p class="landing-section__subtitle">Start free. Upgrade when you need more.</p>
        <div class="landing-pricing">
          ${plans.map(
            (plan) => html`
              <div class="landing-plan ${plan.highlight ? "landing-plan--highlight" : ""}">
                ${plan.badge ? html`<span class="landing-plan__badge">${plan.badge}</span>` : ""}
                <h3 class="landing-plan__name">${plan.name}</h3>
                <div class="landing-plan__price">
                  <span class="landing-plan__amount">${plan.price}</span>
                  <span class="landing-plan__period">${plan.period}</span>
                </div>
                <ul class="landing-plan__features">
                  ${plan.features.map((f) => html`<li>${f}</li>`)}
                </ul>
                <button
                  class="${plan.highlight ? "landing-cta-btn landing-cta-btn--full" : "btn btn--ghost btn--full"}"
                  @click=${this.handleGetStarted}
                >
                  Get Started
                </button>
              </div>
            `,
          )}
        </div>
      </section>
    `;
  }

  // ── Final CTA (full bleed gradient) ──

  private renderFinalCta(): TemplateResult {
    return html`
      <section class="landing-final-cta">
        <div class="landing-final-cta__mesh"></div>
        <h2 class="landing-final-cta__title">
          Ready to add AI to your digital life?
        </h2>
        <p class="landing-final-cta__subtitle">Get started in under a minute. No credit card required.</p>
        <button class="landing-cta-btn landing-cta-btn--light" @click=${this.handleGetStarted}>
          Create Your Free Account
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </button>
      </section>
    `;
  }

  // ── Footer ──

  private renderFooter(): TemplateResult {
    return html`
      <footer class="landing-footer">
        <span class="landing-footer__copy">&copy; 2026 Katanoid</span>
        <nav class="landing-footer__links">
          <a href="/terms" class="landing-footer__link" @click=${this._navigateTo("/terms")}>Terms of Service</a>
          <a href="/privacy" class="landing-footer__link" @click=${this._navigateTo("/privacy")}>Privacy Policy</a>
        </nav>
      </footer>
    `;
  }

  // ── Handlers ──

  private _toggleTheme = (): void => {
    const html = document.documentElement;
    const current = html.getAttribute("data-theme") || "light";
    const next = current === "dark" ? "light" : "dark";
    html.setAttribute("data-theme", next);
    localStorage.setItem("katanoid_theme", next);
  };

  private handleGetStarted = (): void => {
    this.dispatchEvent(new CustomEvent("get-started", { bubbles: true, composed: true }));
  };

  private handleSignIn = (): void => {
    this.dispatchEvent(new CustomEvent("sign-in", { bubbles: true, composed: true }));
  };

  private _navigateTo =
    (path: string) =>
    (e: Event): void => {
      e.preventDefault();
      window.history.pushState(null, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    };
}

declare global {
  interface HTMLElementTagNameMap {
    "katanoid-landing": KatanoidLanding;
  }
}
